import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import {
  ApproveStationRequest,
  BulkSectorSpecialtyRequest,
  CalendarSummaryRequest,
  ConsecutiveHoursCheckCandidate,
  ConsultingRequest,
  CreateStationRequest,
  ListFiltersRequest,
  MatrixSummaryRequest,
  ListStationsFromScheduleRequest,
  MoveStationsRequest,
  Observation,
  PatchStationsFromSchedulePayload,
  PatchStationsRequest,
  PlaceholderAllocationAttemptRequest,
  RelatoryMonthlyExpensesFilter
} from '../Types/Requests/StationRequests'
import {
  BulkSectorSpecialtyResponse,
  CalendarSummaryResponse,
  ConsultingResponse,
  ListStationsFromScheduleResponse,
  ListStationsResponse,
  MatrixSummaryResponse,
  MoveStationsResponse,
  PatchStationsResponse,
  ShiftDetails,
  StationMutationResponse,
  StationsToApproveListResponse
} from '../Types/Responses/StationResponses'

import {
  RelatoryMonthlyExpesesResponse,
  RelatoryYearlyExpesesResponse
} from '../Types/Responses/HealthUnitResponses'
import type { ConsecutiveHoursCheckResponse } from '../Types/Responses/ConsecutiveHoursResponses'
import type { ChangeReasons } from '@/Network/Types/Requests/ChangeReasonRequests'
import type { FilterStates } from '@/components/Filters/useFilterStation'
import { ServiceError, getApiErrorStatus } from '@/utils/apiError'
import { unwrapBlobError } from '@/Network/Requests/blobError'
import {
  EffectiveRegistrationStatus,
  EffectiveSourceKind
} from '@/constants/ds-attendance.constants'
/* ------------------------------------------------------------------------- *
 * F2 — Registro de Atividade
 * ------------------------------------------------------------------------- */

/** 404/405/501 = rota ainda não deployada; o web deploya antes do backend. */
const BACKEND_WITHOUT_ROUTE_STATUSES = [404, 405, 501]

/**
 * Veredito de um candidato no histórico de fontes (M1, balão ④).
 *
 * F4/T6: **"fora do ranking configurado para a escala" é `SUPERSEDED`, não
 * `DISCARDED`.** A fonte sem ranking entra na disputa com a sentinela 99 e pode
 * vencer quando é a única candidata, logo dizer que ela "nem disputou" era
 * falso. `DISCARDED` ficou reservado à incoerência saída &lt; entrada. Quem pintar
 * `DISCARDED` diferente de `SUPERSEDED` precisa saber que o caso mudou de caixa
 * — o texto na tela (`reason`) continua o mesmo.
 *
 * `NO_RECORD` NÃO é emitido pelo backend; fica no union só para não quebrar
 * consumidor antigo que já o testava.
 */
export type StationRegistrationSourceOutcome = 'WON' | 'SUPERSEDED' | 'DISCARDED' | 'NO_RECORD'

/** Horário efetivo de UMA perna do plantão, com a fonte que venceu o ranking. */
/** Uma fonte que perdeu o ranking — alimenta "Histórico de fontes (N superadas)". */
export interface StationRegistrationSupersededSource {
  source: EffectiveSourceKind
  value: string | null
  recordedAt?: string | null
  /** Posição da fonte no ranking de precedência da escala. `99` = fora do ranking. */
  rank?: number | null
  outcome?: StationRegistrationSourceOutcome
  /** Motivo servido pelo backend; sem ele a tela cai no texto por `outcome`. */
  reason?: string | null
  /** Rótulo pt-BR da fonte já pronto pelo backend (`DescribedSource.label`). */
  label?: string | null
}

export interface StationRegistrationLeg {
  value: string | null
  source: EffectiveSourceKind | null
  /** Posição da fonte vencedora no ranking de precedência da escala (1 = topo). */
  sourceRank: number | null
  verifiedAt: string | null
  supersededSources: StationRegistrationSupersededSource[]
  /** Horário previsto da perna; sem ele a tela cai no `startHour`/`endHour` do plantão. */
  expected?: string | null
  /** Fonte existe mas ainda não é final (Registro de Atividade em auditoria). */
  declared?: boolean
}

/**
 * `GET /stations/:id/registration` — o registro da vaga por perna (M1, Tela 1).
 *
 * Tudo o que o §7.3 do spec acrescenta ao recorte mínimo (`expected`,
 * `computedAt`, `ruleVersion`, `frozenAt`, `rank`/`outcome`/`reason` no
 * histórico) é OPCIONAL: o backend da F2 ainda não existe e a tela omite o que
 * não vier. As pernas são anuláveis pelo mesmo motivo — um plantão sem nenhuma
 * fonte não tem perna resolvida a devolver.
 */
/**
 * Trava por perna (backend #3055): plantão com check-in ou check-out registrado
 * pelo app ou validado na auditoria não pode ser alterado nem excluído. A REGRA
 * é do backend — o web só lê este campo para desabilitar e explicar as ações.
 * `reason` já vem em pt-BR, pronto para tooltip/banner.
 */
export interface StationLegLock {
  locked: boolean
  reason: string | null
}

export interface StationRegistration {
  doctorStationId?: number
  status: EffectiveRegistrationStatus
  checkin: StationRegistrationLeg | null
  checkout: StationRegistrationLeg | null
  computedAt?: string | null
  ruleVersion?: number | null
  frozenAt?: string | null
  /** Fuso do plantão (`-03:00`) para a tela formatar sem adivinhar. */
  timezone?: string | null
  /** Rótulo canônico do estado, já em pt-BR, servido pelo backend. */
  statusLabel?: string | null
  /**
   * Sempre preenchido pelo normalizador; opcional no tipo só porque fixtures e
   * telas antigas montam o recorte à mão. Ausente = destravado.
   */
  legLock?: StationLegLock
  /**
   * O payload CRU, exatamente como o backend mandou. Existe porque a tela por
   * perna (T13) precisa de coisas que o recorte achatado acima não carrega —
   * histórico completo, Registros de Atividade da vaga, `frozen.reason` — e
   * porque um campo novo do backend deve chegar à tela sem passar por aqui.
   */
  raw?: StationRegistrationResponse | null
}

/* ------------------------------------------------------------------------- *
 * F4 — o contrato REAL de `GET /stations/:doctorStationId/registration`.
 *
 * Espelho de `station-registration.response.ts` (backend, B16 + T6). Ele NÃO
 * bate com o recorte achatado que a W1 supôs: o backend manda
 * `registrationStatus`, pernas em `legs.{checkin,checkout}` que nunca são
 * nulas, `at` no lugar de `value`, a fonte como OBJETO (`source.kind`) e as
 * superadas numa lista ÚNICA no topo, marcada por perna. `normalizeStationRegistration`
 * é a ponte — as telas de hoje seguem lendo o recorte achatado.
 * ------------------------------------------------------------------------- */

export type StationRegistrationLegName = 'CHECKIN' | 'CHECKOUT'

/** A fonte vencedora de uma perna, já com rótulo e posição no ranking. */
export interface StationRegistrationSourceDto {
  kind: EffectiveSourceKind
  label: string
  method?: string | null
  refType?: string | null
  refId?: number | null
  /** 1-based. `99` = fonte fora do ranking configurado para a escala. */
  rank: number
  /** Container do `ds-attendance` que originou a linha; `null` para app/permanência. */
  dsAttendanceContainerId?: number | null
}

export interface StationRegistrationLegDto {
  /** Horário efetivo, ISO-8601. `null` = perna não resolvida. */
  at: string | null
  /** Previsto da escala, ISO-8601. */
  expected: string | null
  source: StationRegistrationSourceDto | null
  refType?: string | null
  refId?: number | null
}

export interface StationRegistrationHistoryEntry {
  leg: StationRegistrationLegName
  at: string | null
  kind: EffectiveSourceKind
  label: string
  method?: string | null
  refType?: string | null
  refId?: number | null
  rank: number
  outcome: StationRegistrationSourceOutcome
  /** Motivo em pt-BR, pronto para a tela ("superada pelo ... (ranking)"). */
  reason: string
  dsAttendanceContainerId?: number | null
}

export interface StationRegistrationSupersededSourceDto {
  leg: StationRegistrationLegName
  at: string | null
  kind: EffectiveSourceKind
  label: string
  rank: number
  reason: string
}

/** Um Registro de Atividade da vaga. Tipado frouxo de propósito: só T13 o lê. */
export interface StationRegistrationActivityRecord {
  containerId: number
  kind: string
  status: string
  activityType?: string | null
  activityTypeLabel?: string | null
  activityDescription?: string | null
  coveredLegs?: string | null
  coveredLegsLabel?: string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  appliedCheckin?: string | null
  appliedCheckout?: string | null
  attachments?: { evidenceId: number; originalFileName: string; fileSizeKb: number | null }[]
  submittedBy?: { id: number; name: string | null } | null
  submittedAt?: string | null
  reviewedBy?: { id: number; name: string | null } | null
  reviewedAt?: string | null
  rejectionReason?: string | null
  previousContainerId?: number | null
  afterClosure?: boolean
  legacyMigrated?: boolean
  activityRiskScore?: number
  expectedSubmissionAt?: string | null
  isPendingRequest?: boolean
  requestedLeg?: StationRegistrationLegName | null
  requestChannel?: string | null
}

export interface StationRegistrationResponse {
  doctorStationId: number
  scheduleId?: number
  registrationStatus: EffectiveRegistrationStatus
  registrationStatusLabel?: string
  timezone?: string
  computedAt?: string | null
  ruleVersion?: number | null
  /**
   * F4-FX2 — havia política de Registro de Plantão vigente na data do plantão.
   * Campo AUSENTE = binário anterior à FX2; nesse caso o consumidor cai na
   * derivação antiga (`!computedAt && ruleVersion == null`).
   */
  policyInEffect?: boolean
  legacy?: boolean
  frozen?: { at: string | null; reason: string | null }
  frozenAt?: string | null
  legs?: { checkin: StationRegistrationLegDto; checkout: StationRegistrationLegDto }
  /** Aliases de `legs.*` no topo — o backend manda os dois. */
  checkin?: StationRegistrationLegDto
  checkout?: StationRegistrationLegDto
  history?: StationRegistrationHistoryEntry[]
  supersededSources?: StationRegistrationSupersededSourceDto[]
  activityRecords?: StationRegistrationActivityRecord[]
  pendingRequest?: StationRegistrationActivityRecord | null
  manualCheckBlockEnabled?: boolean
  /** Campo aditivo (backend #3055). AUSENTE = backend anterior: destravado. */
  legLock?: StationLegLock
}

/** O que chega no fio: o contrato real OU o recorte achatado de um backend antigo. */
type StationRegistrationPayload = Partial<StationRegistrationResponse> &
  Partial<StationRegistration>

const LEG_NAME: Record<'checkin' | 'checkout', StationRegistrationLegName> = {
  checkin: 'CHECKIN',
  checkout: 'CHECKOUT'
}

function buildLeg(
  key: 'checkin' | 'checkout',
  raw: StationRegistrationPayload
): StationRegistrationLeg | null {
  const dto = (raw.legs?.[key] ?? raw[key]) as StationRegistrationLegDto | undefined
  // Backend antigo já mandava a perna no formato da tela — passa direto.
  if (!dto) return null
  if (!('at' in dto)) return dto as StationRegistrationLeg

  const superseded = (raw.supersededSources ?? [])
    .filter((item) => item.leg === LEG_NAME[key])
    .map((item) => ({
      source: item.kind,
      value: item.at ?? null,
      rank: item.rank ?? null,
      // A lista `supersededSources` do backend é, por definição, o recorte das
      // PERDEDORAS — o veredito exato de cada uma está em `history`.
      outcome: 'SUPERSEDED' as const,
      reason: item.reason ?? null,
      label: item.label ?? null
    }))

  return {
    value: dto.at ?? null,
    source: dto.source?.kind ?? null,
    sourceRank: dto.source?.rank ?? null,
    // O backend NÃO serve verificação nesta rota (T6, escopo não feito): o
    // estado vem de `GET /doctor-checks/:checkId/verification`, e derivar um
    // `verified` aqui seria uma segunda cópia da regra. `refId` da perna é o
    // endereço do check para quem quiser chamar aquela rota.
    verifiedAt: null,
    supersededSources: superseded,
    expected: dto.expected ?? null
  }
}

/**
 * Traduz o payload do backend para o recorte que as telas de hoje leem, sem
 * perder nada: o cru viaja em `raw`.
 *
 * Aceita os dois formatos de propósito — o web deploya antes do backend e há
 * fixtures/telas escritas contra o recorte achatado da W1. Um payload que já
 * traga `status` passa inteiro.
 */
export function normalizeStationRegistration(
  payload: StationRegistrationPayload | null | undefined
): StationRegistration | null {
  if (!payload) return null

  const status = payload.registrationStatus ?? payload.status
  // Sem `registrationStatus` e com `status`: é o recorte achatado da W1 (ou uma
  // fixture escrita contra ele). Guardá-lo em `raw` prometeria a T13 um contrato
  // que aquele payload não cumpre.
  const isLegacyShape = payload.registrationStatus === undefined && payload.status !== undefined

  return {
    doctorStationId: payload.doctorStationId,
    status: status ?? EffectiveRegistrationStatus.NONE,
    checkin: buildLeg('checkin', payload),
    checkout: buildLeg('checkout', payload),
    ...registrationMetadata(payload),
    raw: isLegacyShape ? null : (payload as StationRegistrationResponse)
  }
}

/** Campos escalares do envelope. Separado só para manter o normalizador legível. */
function registrationMetadata(payload: StationRegistrationPayload) {
  return {
    statusLabel: payload.registrationStatusLabel ?? null,
    computedAt: payload.computedAt ?? null,
    ruleVersion: payload.ruleVersion ?? null,
    // `frozenAt` é atalho de `frozen.at` no contrato; ler os dois evita depender
    // de qual deles a versão do backend preencheu.
    frozenAt: payload.frozenAt ?? payload.frozen?.at ?? null,
    timezone: payload.timezone ?? null,
    legLock: normalizeStationLegLock(payload.legLock)
  }
}

/**
 * Default seguro = destravado: backend anterior ao #3055 (sem o campo) ou shape
 * inesperado não podem travar a tela — o backend continua recusando de todo modo.
 */
export function normalizeStationLegLock(raw: unknown): StationLegLock {
  const candidate = raw as { locked?: unknown; reason?: unknown } | null | undefined
  if (candidate?.locked !== true) return { locked: false, reason: null }
  const reason = typeof candidate.reason === 'string' ? candidate.reason.trim() : ''
  return { locked: true, reason: reason || null }
}

/**
 * O `origin` de `histories[]` é DEPRECADO desde a F2: o backend passou a gravar
 * `WEB` sozinho (Q18 — origem e método nunca vêm do cliente) e ignora o que o
 * front mandar. Removê-lo aqui, e não em cada tela, garante que nenhuma edição de
 * plantão volte a sugerir ao backend qual é a origem do check.
 *
 * O payload viaja em `data` como JSON string dentro do FormData; um `data`
 * ausente ou que não seja JSON passa intacto — este método nunca pode ser o
 * motivo de uma edição de plantão falhar.
 */
function stripDeprecatedHistoryOrigin(payload: FormData): FormData {
  const raw = payload.get('data')
  if (typeof raw !== 'string') return payload

  try {
    const parsed = JSON.parse(raw) as { histories?: Record<string, unknown>[] }
    if (!Array.isArray(parsed.histories)) return payload

    parsed.histories = parsed.histories.map((history) => {
      const withoutOrigin = { ...history }
      delete withoutOrigin.origin
      return withoutOrigin
    })
    payload.set('data', JSON.stringify(parsed))
  } catch (e) {
    console.debug('[Station] payload de edição fora do formato esperado; enviado intacto', e)
  }

  return payload
}

/**
 * Contrato: `coverageStatus` viaja como lista CSV (`OPEN,UNIDENTIFIED`), igual à
 * grade. O `FilterStates` da Consulta guarda um array — sem isto o axios mandaria
 * `coverageStatus[]=...`, que o backend não lê.
 */
export function serializeCoverageStatusFilter<T extends { coverageStatus?: unknown }>(
  filters: T
): Omit<T, 'coverageStatus'> & { coverageStatus?: string } {
  const { coverageStatus, ...rest } = filters
  if (!Array.isArray(coverageStatus))
    return filters as Omit<T, 'coverageStatus'> & { coverageStatus?: string }
  return coverageStatus.length ? { ...rest, coverageStatus: coverageStatus.join(',') } : rest
}

export default class StationService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = '/stations'
  }

  // O backend anexa `overlapAlerts` ao plantao criado (conflito de agenda detectado
  // dentro da transacao de escrita) — ver `StationMutationResponse`.
  // Erro JÁ normalizado (ServiceError), como update/delete: os guards do modal de
  // criação (duração, horas consecutivas) fazem unwrap do originalError — o axios
  // cru não é mais contrato deste método.
  async create(payload: CreateStationRequest): Promise<StationMutationResponse> {
    try {
      const { data } = await request.post<StationMutationResponse>(
        `${this.getCompletePath()}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar o plantão')
    }
  }

  async list(filters?: ListFiltersRequest): Promise<ListStationsResponse> {
    try {
      const { data } = await request.get<ListStationsResponse>(this.getCompletePath(), filters)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os plantões')
    }
  }

  async getConsultingList(filters: ConsultingRequest): Promise<ConsultingResponse> {
    try {
      const columnsFilters = serializeCoverageStatusFilter({ ...filters.columnFilters })
      const query = {
        ...columnsFilters,
        sort: filters.sort,
        page: filters.page,
        perPage: filters.perPage
      }
      const { data } = await request.get<ConsultingResponse>(
        this.getCompletePath() + '/consulting',
        query
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a lista de plantões')
    }
  }

  async exportRelatoryMedicXlsx(filters: ConsultingRequest): Promise<HTMLAnchorElement> {
    const columnsFilters = serializeCoverageStatusFilter({ ...filters.columnFilters })
    const query = {
      ...columnsFilters,
      sort: filters.sort,
      page: filters.page,
      perPage: filters.perPage,
      exportXLSX: true
    }
    let data: BlobPart
    try {
      ;({ data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/relatory-medic`,
        query,
        undefined,
        'blob'
      ))
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o relatório médico'
      )
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'relatório-médico.xlsx')
    return link
  }

  async exportRelatoryResumeMedicXlsx(filters: ConsultingRequest): Promise<HTMLAnchorElement> {
    const columnsFilters = serializeCoverageStatusFilter({ ...filters.columnFilters })
    const query = {
      ...columnsFilters,
      sort: filters.sort,
      page: filters.page,
      perPage: filters.perPage,
      exportXLSX: true
    }
    let data: BlobPart
    try {
      ;({ data } = await request.get<BlobPart>(
        `${this.getCompletePath()}/relatory-resume-medic`,
        query,
        undefined,
        'blob'
      ))
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o relatório médico resumido'
      )
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'relatório-médico-resumido.xlsx')
    return link
  }

  // Idem `create`: a resposta traz o plantao atualizado + `overlapAlerts`.
  // Diferente do `create`, aqui o erro JÁ é normalizado (ServiceError): os guards
  // do modal de edição (duração, check-in, pós-plantão, horas consecutivas) fazem
  // unwrap do originalError — o axios cru não é mais contrato deste método.
  async update(
    stationId: number,
    payload: FormData,
    scheduleId: number
  ): Promise<StationMutationResponse> {
    try {
      const { data } = await request.put<StationMutationResponse>(
        `${this.getCompletePath()}/${stationId}/schedule/${scheduleId}`,
        stripDeprecatedHistoryOrigin(payload),
        { 'Content-Type': 'multipart/form-data' }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível editar o plantão')
    }
  }

  /**
   * F2/F4 — registro da vaga POR PERNA, com fonte, veredito e posição no ranking
   * (M1, Tela 1). Tolerante a backend antigo: `null` quando a rota ainda não
   * existe, e a tela cai no bloco de registro de hoje.
   *
   * A resposta passa por `normalizeStationRegistration` porque o contrato real
   * (`registrationStatus`, `legs.*.at`, `source` como objeto, superadas numa
   * lista única) não é o recorte que as telas leem. O cru fica em `.raw`.
   */
  async getStationRegistration(doctorStationId: number): Promise<StationRegistration | null> {
    try {
      const { data } = await request.get<StationRegistrationPayload>(
        `${this.getCompletePath()}/${doctorStationId}/registration`
      )
      return normalizeStationRegistration(data)
    } catch (e) {
      if (BACKEND_WITHOUT_ROUTE_STATUSES.includes(getApiErrorStatus(e) ?? 0)) {
        console.debug('[Station] rota de registro indisponível; tela segue sem ela', e)
        return null
      }
      throw this.handleError(e, 'Não foi possível carregar o registro deste plantão')
    }
  }

  async patchStations(
    stationIds: number[],
    payload: PatchStationsRequest
  ): Promise<PatchStationsResponse> {
    try {
      const { data } = await request.patch<PatchStationsResponse>(
        `${this.getCompletePath()}/bulk?ids=${stationIds}`,
        payload
      )
      return data && typeof data === 'object' ? data : {}
    } catch (e) {
      throw this.handleError(e, 'Não foi possível editar os plantões')
    }
  }

  async patchAllStations(
    payload: PatchStationsRequest,
    filters?: FilterStates
  ): Promise<PatchStationsResponse> {
    try {
      const { data } = await request.patch<PatchStationsResponse>(
        `${this.getCompletePath()}/bulk/all`,
        payload,
        undefined,
        filters ? serializeCoverageStatusFilter(filters) : filters
      )
      return data && typeof data === 'object' ? data : {}
    } catch (e) {
      throw this.handleError(e, 'Não foi possível editar os plantões')
    }
  }

  /**
   * Mover plantões para outra escala (`PATCH /stations/bulk/move`): 1 request para a seleção
   * toda. 403/400 são tudo-ou-nada no BE; a mensagem dele chega ao toast via `handleError`.
   */
  async moveStations(ids: number[], targetScheduleId: number): Promise<MoveStationsResponse> {
    try {
      const body: MoveStationsRequest = { ids, targetScheduleId }
      const { data } = await request.patch<Partial<MoveStationsResponse>>(
        `${this.getCompletePath()}/bulk/move`,
        body
      )
      const result = data && typeof data === 'object' ? data : {}
      return {
        moved: result.moved ?? 0,
        sectorCleared: result.sectorCleared ?? 0,
        specialtyCleared: result.specialtyCleared ?? 0
      }
    } catch (e) {
      throw this.handleError(e, 'Não foi possível mover os plantões')
    }
  }

  async bulkSectorSpecialty(
    body: BulkSectorSpecialtyRequest
  ): Promise<BulkSectorSpecialtyResponse> {
    try {
      const { data } = await request.patch<BulkSectorSpecialtyResponse>(
        `${this.getCompletePath()}/bulk/sector-specialty`,
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível definir setor e especialidade dos plantões')
    }
  }

  async calendarSummary(filters: CalendarSummaryRequest): Promise<CalendarSummaryResponse> {
    try {
      const { data } = await request.get<CalendarSummaryResponse>(
        `${this.getCompletePath()}/calendar-summary`,
        filters
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o calendário de cobertura')
    }
  }

  /** Matriz Setor × Especialidade (Onda 2.1). */
  async matrixSummary(filters: MatrixSummaryRequest): Promise<MatrixSummaryResponse> {
    try {
      const { data } = await request.get<MatrixSummaryResponse>(
        `${this.getCompletePath()}/matrix-summary`,
        filters
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar a matriz')
    }
  }

  async bulkDelete(stationIds: number[], changeReasons?: ChangeReasons): Promise<void> {
    try {
      await request.delete(
        `${this.getCompletePath()}/bulk?ids=${stationIds}`,
        changeReasons ? { changeReasons } : undefined
      )
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover os plantões')
    }
  }

  async delete(
    stationId: number,
    scheduleId: number,
    changeReasons?: ChangeReasons
  ): Promise<void> {
    try {
      const { data } = await request.delete(
        `${this.getCompletePath()}/${stationId}/schedule/${scheduleId}`,
        changeReasons ? { changeReasons } : undefined
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover o plantão')
    }
  }

  async exportRelatoryXlsx(filters: {
    startDate: Date
    endDate: Date
    filename: string
    healthUnits?: string[]
    schedules?: string[]
    startHour?: string
    endHour?: string
  }): Promise<HTMLAnchorElement> {
    const startISO = filters.startDate.toISOString()
    const endISO = filters.endDate.toISOString()

    const qs = new URLSearchParams()
    ;(filters.healthUnits ?? []).forEach((u) => qs.append('healthUnits', String(u)))
    ;(filters.schedules ?? []).forEach((s) => qs.append('schedules', String(s)))
    if (filters.startHour) {
      filters.startHour
        .split(',')
        .filter(Boolean)
        .forEach((h) => qs.append('startHour', h))
    }
    if (filters.endHour) {
      filters.endHour
        .split(',')
        .filter(Boolean)
        .forEach((h) => qs.append('endHour', h))
    }

    const queryString = qs.toString()
    const urlPath = `${this.getCompletePath()}/relatory/${startISO}&${endISO}${queryString ? `?${queryString}` : ''}`

    let data: BlobPart
    try {
      ;({ data } = await request.get<BlobPart>(urlPath, undefined, undefined, 'blob'))
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o relatório de plantões'
      )
    }
    const url = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filters.filename)
    return link
  }

  async exportStationsXlsx(filters: {
    startDate: Date | string
    endDate: Date | string
    healthUnits?: Array<number | string>
    schedules?: Array<number | string>
    filename: string
    ourTeam?: string
    valueMin?: string
    valueMax?: string
    paymentValueMin?: string
    paymentValueMax?: string
    inCash?: string
    advanceRequested?: string
    advancePaid?: string
    status?: string
    startHour?: string
    endHour?: string
    operationalGroups?: string[]
    stationType?: string[]
    openedStation?: string
    doctorPending?: string
    withCheckin?: string
    withCheckout?: string
    doctorIds?: string[] | string
  }): Promise<HTMLAnchorElement> {
    const toISO = (d: Date | string) => (d instanceof Date ? d.toISOString() : String(d))

    const qs = new URLSearchParams()
    qs.set('type', 'GENERAL')
    qs.set('startDate', toISO(filters.startDate))
    qs.set('endDate', toISO(filters.endDate))
    ;(filters.healthUnits ?? []).forEach((u) => qs.append('healthUnits[]', String(u)))
    ;(filters.schedules ?? []).forEach((s) => qs.append('schedules[]', String(s)))
    ;(filters.operationalGroups ?? []).forEach((g) => qs.append('operationalGroups[]', g))
    ;(filters.stationType ?? []).forEach((t) => qs.append('stationType[]', t))

    const scalars: Record<string, string | undefined> = {
      ourTeam: filters.ourTeam,
      valueMin: filters.valueMin,
      valueMax: filters.valueMax,
      paymentValueMin: filters.paymentValueMin,
      paymentValueMax: filters.paymentValueMax,
      inCash: filters.inCash,
      advanceRequested: filters.advanceRequested,
      advancePaid: filters.advancePaid,
      status: filters.status,
      startHour: filters.startHour,
      endHour: filters.endHour,
      openedStation: filters.openedStation,
      doctorPending: filters.doctorPending,
      withCheckin: filters.withCheckin,
      withCheckout: filters.withCheckout
    }
    for (const [key, val] of Object.entries(scalars)) {
      if (val != null && val !== '') qs.set(key, val)
    }

    const urlPath = `${this.getCompletePath()}/stationsxlsx?${qs.toString()}`

    let data: BlobPart
    try {
      ;({ data } = await request.get<BlobPart>(urlPath, undefined, undefined, 'blob'))
    } catch (e) {
      throw this.handleError(
        await unwrapBlobError(e),
        'Não foi possível exportar o relatório de plantões'
      )
    }

    const blobUrl = window.URL.createObjectURL(new Blob([data]))
    const link = document.createElement('a')
    link.href = blobUrl
    link.setAttribute('download', filters.filename)
    return link
  }

  async relatoryMonthlyExpenses(
    filters?: RelatoryMonthlyExpensesFilter
  ): Promise<RelatoryMonthlyExpesesResponse> {
    try {
      const { data } = await request.get<RelatoryMonthlyExpesesResponse>(
        `${this.getCompletePath()}/monthly-expenses`,
        filters
      )
      return data
    } catch (e) {
      // Antes: `return []` — erro indistinguível de "sem gastos no mês".
      // O consumidor (MonthlyReport.vue) exibe ReportErrorState com retry.
      throw this.handleError(e, 'Não foi possível carregar o relatório mensal de gastos')
    }
  }

  async relatoryYearlyExpenses(healthUnits?: string[]): Promise<RelatoryYearlyExpesesResponse[]> {
    try {
      const { data } = await request.get<RelatoryYearlyExpesesResponse[]>(
        `${this.getCompletePath()}/yearly-expenses`,
        {
          healthUnits
        }
      )
      return data
    } catch (e) {
      // Antes: `return []` — erro indistinguível de "sem gastos no ano".
      // O consumidor (YearlyReport.vue) exibe ReportErrorState com retry.
      throw this.handleError(e, 'Não foi possível carregar o relatório anual de gastos')
    }
  }

  async getStationsToApprove(filters: ConsultingRequest): Promise<StationsToApproveListResponse> {
    try {
      const query = {
        ...filters.columnFilters,
        sort: filters.sort,
        page: filters.page,
        perPage: filters.perPage
      }
      const { data } = await request.get<StationsToApproveListResponse>(
        `${this.getCompletePath()}/approve`,
        query
      )
      return data
    } catch (e) {
      // Antes: `return { rows: [], totalRecords: 0 }` — falha de load parecia
      // "nenhum plantão a aprovar". A store expõe loadError e a view mostra retry.
      throw this.handleError(e, 'Não foi possível carregar os plantões aguardando aprovação')
    }
  }

  async approveStation(payload: ApproveStationRequest): Promise<void> {
    try {
      await request.post<void>(`${this.getCompletePath()}/approve`, payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível aprovar o pagamento do plantão')
    }
  }

  async getStationDetails(stationId: number): Promise<ShiftDetails> {
    try {
      const { data } = await request.get<ShiftDetails>(`${this.getCompletePath()}/${stationId}`)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os detalhes do plantão')
    }
  }

  async getStationObservations(stationId: number): Promise<Observation[]> {
    try {
      const { data } = await request.get<Observation[]>(
        `${this.getCompletePath()}/${stationId}/observations`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as observações do plantão')
    }
  }

  async createStationObservation(
    stationId: number,
    observation: { description: string; userName: string }
  ): Promise<Observation[]> {
    try {
      const { data } = await request.post<Observation[]>(
        `${this.getCompletePath()}/${stationId}/observations`,
        observation
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível criar a observação')
    }
  }

  async deleteStationObservation(stationId: number, index: number): Promise<void> {
    if (typeof stationId !== 'number' || typeof index !== 'number') {
      // Throw manual de validação: sempre ServiceError (mensagem exibível).
      throw new ServiceError('Parâmetros inválidos ao deletar observação')
    }

    try {
      await request.delete(`${this.getCompletePath()}/${stationId}/observations/${index}`)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível excluir a observação')
    }
  }

  async updateStationObservation(
    stationId: number,
    index: number,
    observation: { description: string }
  ) {
    try {
      const { data } = await request.patch(
        `${this.getCompletePath()}/${stationId}/observations/${index}`,
        observation
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível editar a observação')
    }
  }

  async bulkDeleteByFilter(
    filters: ConsultingRequest['columnFilters'],
    changeReasons?: ChangeReasons
  ): Promise<void> {
    try {
      const body = filters ? serializeCoverageStatusFilter(filters) : filters
      await request.post(
        `${this.getCompletePath()}/bulk/delete`,
        changeReasons ? { ...body, changeReasons } : body
      )
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover os plantões')
    }
  }

  async listStationsFromSchedule(
    filters: ListStationsFromScheduleRequest
  ): Promise<ListStationsFromScheduleResponse> {
    try {
      const { data } = await request.get<ListStationsFromScheduleResponse>(
        `${this.getCompletePath()}/listStationsfromschedule`,
        filters
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os plantões da escala')
    }
  }

  /**
   * Envelope de operações por médico (`PATCH /stations/bulk`). Devolve o que o BE limpou
   * ao trocar de escala (`sectorCleared`/`specialtyCleared`, H8); BE antigo sem corpo → `{}`.
   */
  async patchStationsFromSchedule(
    payload: PatchStationsFromSchedulePayload
  ): Promise<PatchStationsResponse> {
    try {
      const { data } = await request.patch<PatchStationsResponse>(
        `${this.getCompletePath()}/bulk`,
        payload
      )
      return data && typeof data === 'object' ? data : {}
    } catch (e) {
      throw this.handleError(e, 'Não foi possível editar os plantões da escala')
    }
  }

  // Preview do limite de horas consecutivas. Não escreve nada: os endpoints de escrita
  // revalidam a mesma regra dentro da transação (o preview é UX, não é o guard).
  async consecutiveHoursCheck(
    candidates: ConsecutiveHoursCheckCandidate[]
  ): Promise<ConsecutiveHoursCheckResponse> {
    try {
      const { data } = await request.post<ConsecutiveHoursCheckResponse>(
        `${this.getCompletePath()}/consecutive-hours/check`,
        { candidates }
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar o limite de horas consecutivas')
    }
  }

  /**
   * Telemetria do aviso do médico coringa aberto de forma PREVENTIVA (antes de enviar).
   * Quem chama não aguarda (fire-and-forget, ver `reportPreventivePlaceholderAttempt`).
   */
  async reportPlaceholderAllocationAttempt(
    payload: PlaceholderAllocationAttemptRequest
  ): Promise<void> {
    try {
      await request.post(`${this.getCompletePath()}/placeholder-allocation-attempts`, payload)
    } catch (e) {
      throw this.handleError(e, 'Não foi possível registrar a tentativa de alocação')
    }
  }
}
